import { ReactNode, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { useBlocker } from 'react-router-dom';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { AlertOrangeIcon } from '../icons';

interface UnsavedChangesState {
  isDirty: boolean;
  message: string;
  setUnsavedChanges: (dirty: boolean, message?: string) => void;
  reset: () => void;
}

const persistMiddleware = persist<UnsavedChangesState>(
  set => {
    return {
      isDirty: false,
      message: `You haven't saved your changes in the previous step. You must click "Save" at each step to save the data.`,
      setUnsavedChanges: (dirty, message) =>
        set(state => ({
          isDirty: dirty,
          message: message ?? state.message,
        })),
      reset: () => set({ isDirty: false }),
    };
  },
  {
    name: 'unsaved-changes',
    storage: createJSONStorage(() => localStorage),
  },
);

export const useUnsavedChangesStore = create(persistMiddleware);

export function useUnsavedChangesWatcher() {
  const isDirty = useUnsavedChangesStore(state => state.isDirty);
  const message = useUnsavedChangesStore(state => state.message);
  const reset = useUnsavedChangesStore(state => state.reset);
  const didProceed = useRef(false);

  const blocker = useBlocker(isDirty);

  useEffect(() => {
    if (blocker.state === 'blocked' && isDirty && !didProceed.current) {
      toast('dismiss', { id: 'unsaved_changes' });
      toast(
        t => (
          <span className="flex flex-col justify-center items-center gap-2 text-sm">
            <span>
              <img src={AlertOrangeIcon} className="w-8 h-8" alt="alert" />
            </span>
            {message}
            <button
              className="px-2 py-1 border rounded hover:border-blue-800"
              onClick={() => {
                toast.dismiss(t.id);
                if (blocker.state === 'blocked') {
                  blocker.reset();
                  reset();
                }
              }}
            >
              OK
            </button>
          </span>
        ),
        {
          duration: 10000,
          id: 'unsaved_changes',
        },
      );
      didProceed.current = true;
      blocker.proceed();
      reset();
    }
    if (blocker.state === 'unblocked') {
      didProceed.current = false;
    }
  }, [blocker.state, isDirty, message]);

  return { reset };
}

interface NavigateBlockerProviderProps {
  children: ReactNode;
}
export const NavigateBlockerProvider = ({ children }: NavigateBlockerProviderProps) => {
  useUnsavedChangesWatcher();
  return <>{children}</>;
};
